import { BikeState } from '../sim/bike';
import { CenterLine, LinePoint } from '../sim/centerline';
import { Placement } from '../sim/level';

/** s between remembered points. */
const EVERY = 0.25;
/** Points kept (the last 3 s). */
const KEEP = 12;
/** A fall puts the rider back where he was at least this long before it (not on the approach that went wrong). */
const BEFORE_FALL = 1.5;

/**
 * Where to put a rider back after a fall: the last spot he rode through safely (both wheels down, fairly upright,
 * rolling), from a little before the crash. On a level with a centreline the heading is the track's direction there;
 * elsewhere the way he was going. Plain numbers in a ring: it is updated every physics step and allocates nothing.
 */
export class SafeTrail {
  private readonly xs = new Float64Array(KEEP);
  private readonly zs = new Float64Array(KEEP);
  private readonly hs = new Float64Array(KEEP);
  private readonly ts = new Float64Array(KEEP);
  private count = 0;
  private head = 0;
  private lastT = -Infinity;
  private hint = NaN;
  private readonly found: LinePoint = { s: 0, d: 0, index: 0 };

  constructor(private readonly line: CenterLine | null) {}

  reset(): void { this.count = 0; this.head = 0; this.lastT = -Infinity; this.hint = NaN; }

  /** Remembers a spot as safe (the rider was just put back there). */
  mark(at: Placement, time: number): void {
    const i = this.head;
    this.xs[i] = at.x; this.zs[i] = at.z; this.hs[i] = at.heading; this.ts[i] = time;
    this.head = (i + 1) % KEEP;
    this.count = Math.min(KEEP, this.count + 1);
    this.lastT = time;
  }

  /** Call after every physics step. */
  update(bike: BikeState, time: number): void {
    const tel = bike.telemetry;
    if (time - this.lastT < EVERY) return;
    const safe = !tel.fallen && !tel.airborne && bike.wheels[0].contact && bike.wheels[1].contact && bike.crashIn <= 0
      && Math.abs(tel.roll) < 0.35 && Math.abs(tel.pitch) < 0.35 && Math.abs(tel.speed) > 1;
    if (!safe) return;
    this.lastT = time;
    const v = bike.velocity;
    let heading = Math.atan2(v.x, v.z);
    const line = this.line;
    if (line) {
      if (Number.isNaN(this.hint)) { const c = line.closest(bike.position.x, bike.position.z); this.found.s = c.s; }
      else line.closestInto(bike.position.x, bike.position.z, this.hint, 30, this.found);
      this.hint = this.found.s;
      // The track's direction there, the way the rider was going along it.
      const along = line.tangentHeading(this.found.s);
      heading = Math.cos(along - heading) >= 0 ? along : along + Math.PI;
    }
    const i = this.head;
    this.xs[i] = bike.position.x; this.zs[i] = bike.position.z; this.hs[i] = heading; this.ts[i] = time;
    this.head = (i + 1) % KEEP;
    this.count = Math.min(KEEP, this.count + 1);
  }

  /** The newest remembered point at least BEFORE_FALL older than `time` (else the oldest one), or null. */
  pick(time: number): Placement | null {
    if (this.count === 0) return null;
    let best = -1;
    for (let k = 1; k <= this.count; k++) {
      const i = (this.head - k + KEEP) % KEEP;
      best = i;
      if (time - this.ts[i]! >= BEFORE_FALL) break;
    }
    return { x: this.xs[best]!, z: this.zs[best]!, heading: this.hs[best]! };
  }
}
